import { useState, useEffect, useCallback } from 'react';

const BOUNDS_BUFFER_FACTOR = 0.15;

/**
 * Expands LatLngBounds by a percentage buffer on each side to reduce
 * polygon pop-in/out when panning near viewport edges.
 */
export function expandBounds(bounds: google.maps.LatLngBounds, factor: number): google.maps.LatLngBounds {
	const ne = bounds.getNorthEast();
	const sw = bounds.getSouthWest();

	const latRange = ne.lat() - sw.lat();
	const lngRange = ne.lng() - sw.lng();

	const latBuffer = latRange * factor;
	const lngBuffer = lngRange * factor;

	return new google.maps.LatLngBounds(
		new google.maps.LatLng(sw.lat() - latBuffer, sw.lng() - lngBuffer),
		new google.maps.LatLng(ne.lat() + latBuffer, ne.lng() + lngBuffer)
	);
}

/**
 * Hook that tracks the current map viewport bounds and zoom level separately.
 * Only listens to the 'idle' event (fires after user stops panning/zooming),
 * so no updates occur mid-interaction.
 *
 * Bounds and zoom are split into separate state to allow effects that depend
 * on only one of them to avoid unnecessary re-runs.
 */
export function useServiceAreaMapView(map: google.maps.Map | null): {
	bounds: google.maps.LatLngBounds | null;
	zoom: number;
} {
	const [viewBounds, setViewBounds] = useState<google.maps.LatLngBounds | null>(null);
	const [viewZoom, setViewZoom] = useState<number>(0);

	const updateViewState = useCallback(() => {
		if (!map) return;

		const rawBounds = map.getBounds();
		const zoom = map.getZoom();

		if (!rawBounds) return;

		const bufferedBounds = expandBounds(rawBounds, BOUNDS_BUFFER_FACTOR);
		setViewBounds(bufferedBounds);
		// Only update zoom if it actually changed (prevents Effect 3 from re-running on pure pans)
		setViewZoom(prev => (prev === zoom ? prev : zoom));
	}, [map]);

	useEffect(() => {
		if (!map) return;

		// Set initial state when map is ready
		updateViewState();

		// Only listen to 'idle' -- fires after user stops interacting (pan or zoom complete).
		// No mid-drag updates, no debounce needed.
		const idleListener = map.addListener('idle', updateViewState);

		return () => {
			google.maps.event.removeListener(idleListener);
		};
	}, [map, updateViewState]);

	return { bounds: viewBounds, zoom: viewZoom };
}
